import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function LockingRewardsPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-pink-50 via-white to-rose-50">
        <div className="max-w-5xl mx-auto text-center">
          <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center mx-auto mb-8">
            <i className="ri-lock-line text-5xl text-white"></i>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            ロッキングリワード マニュアル
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            トークンをロックして報酬を獲得する方法を詳しく解説します
          </p>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-10 md:p-14 border border-pink-100">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              ロッキングリワードとは？
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              ロッキングリワードは、Mawariトークンを一定期間ロック（固定）することで、追加の報酬トークンを獲得できる仕組みです。トークンをロックすることで、プロジェクトの長期的な成長に貢献し、その対価として魅力的な報酬を受け取ることができます。
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">安全性</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  スマートコントラクトによる安全な管理
                </p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center mb-4">
                  <i className="ri-line-chart-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">高利回り</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  ロック期間に応じた魅力的な報酬率
                </p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center mb-4">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">柔軟性</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  複数のロック期間から選択可能
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Step by Step Guide */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 text-center">
            ロッキング手順
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16">
            簡単な4ステップで報酬獲得を開始できます
          </p>

          <div className="space-y-8">
            {/* Step 1 */}
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">ウォレットを接続</h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-4">
                    MetaMaskなどの対応ウォレットをMawari FREAKプラットフォームに接続します。ウォレットに十分なMawariトークンがあることを確認してください。
                  </p>
                  <div className="bg-pink-50 rounded-xl p-4 border border-pink-100">
                    <p className="text-base text-gray-700">
                      <strong className="text-pink-600">ヒント：</strong> 初めての方は、まずテストネットで操作に慣れることをおすすめします。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">ロック期間を選択</h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-4">
                    ロック期間を選択します。期間が長いほど、より高い報酬率が適用されます。
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-5 border border-pink-100">
                      <div className="text-3xl font-bold text-pink-600 mb-2">30日</div>
                      <div className="text-base text-gray-700">年利 8%</div>
                    </div>
                    <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-5 border border-pink-100">
                      <div className="text-3xl font-bold text-pink-600 mb-2">90日</div>
                      <div className="text-base text-gray-700">年利 15%</div>
                    </div>
                    <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-5 border border-pink-100">
                      <div className="text-3xl font-bold text-pink-600 mb-2">180日</div>
                      <div className="text-base text-gray-700">年利 25%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">ロック数量を入力</h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-4">
                    ロックしたいMawariトークンの数量を入力します。最低ロック数量は100トークンです。入力後、予想される報酬額が自動的に表示されます。
                  </p>
                  <div className="bg-pink-50 rounded-xl p-4 border border-pink-100">
                    <p className="text-base text-gray-700">
                      <strong className="text-pink-600">注意：</strong> ガス代として少量のETHをウォレットに残しておいてください。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">トランザクションを承認</h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-4">
                    「ロックする」ボタンをクリックし、ウォレットでトランザクションを承認します。承認後、トークンは指定期間ロックされ、報酬の獲得が開始されます。
                  </p>
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <p className="text-base text-gray-700">
                      <strong className="text-green-600">完了！</strong> ダッシュボードでロック状況と獲得報酬をいつでも確認できます。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rewards Claiming */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 text-center">
            報酬の受け取り方
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16">
            ロック期間終了後の報酬受け取り手順
          </p>

          <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl p-10 md:p-14 border border-pink-100">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center">
                  <i className="ri-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">自動計算</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    報酬は毎日自動的に計算され、ダッシュボードに表示されます。リアルタイムで獲得状況を確認できます。
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center">
                  <i className="ri-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">ロック期間終了</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    ロック期間が終了すると、元本と報酬の両方を引き出すことができるようになります。
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center">
                  <i className="ri-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">報酬の請求</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    ダッシュボードの「報酬を請求」ボタンをクリックし、ウォレットでトランザクションを承認すると、報酬がウォレットに送金されます。
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center">
                  <i className="ri-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">再ロックオプション</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    報酬を受け取った後、元本と報酬を合わせて再度ロックすることで、複利効果を得ることができます。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 text-center">
            重要な注意事項
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16">
            ロッキングリワードを利用する前に必ずお読みください
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                <i className="ri-alert-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">ロック期間中の引き出し不可</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                ロック期間中は、トークンを引き出すことができません。期間終了まで待つ必要があります。
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 rounded-xl bg-yellow-100 flex items-center justify-center mb-4">
                <i className="ri-error-warning-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">スマートコントラクトリスク</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                スマートコントラクトは監査済みですが、完全にリスクがないわけではありません。自己責任でご利用ください。
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                <i className="ri-information-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">報酬率の変動</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                報酬率は市場状況により変動する可能性があります。ロック時の条件が適用されます。
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 rounded-xl bg-green-100 flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">セキュリティ対策</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                公式サイトからのみアクセスし、秘密鍵やシードフレーズは絶対に他人と共有しないでください。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 text-center">
            よくある質問
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16">
            ロッキングリワードに関するよくある質問
          </p>

          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">最低ロック数量はありますか？</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                はい、最低ロック数量は100 Mawariトークンです。これにより、ガス代に対して効率的な報酬を得ることができます。
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">複数のロックを同時に行えますか？</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                はい、異なる期間や数量で複数のロックを同時に行うことができます。それぞれ独立して管理されます。
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">報酬はいつ受け取れますか？</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                報酬は毎日計算されますが、実際に引き出せるのはロック期間終了後です。期間終了後はいつでも請求できます。
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">ガス代はどのくらいかかりますか？</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                ガス代はネットワークの混雑状況により変動します。通常、ロック時と報酬請求時にそれぞれガス代が発生します。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-pink-500 via-purple-500 to-pink-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            今すぐロッキングを始めましょう
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Mawariトークンをロックして、魅力的な報酬を獲得しましょう
          </p>
          <button className="px-12 py-5 bg-white text-pink-600 text-xl rounded-full font-bold hover:shadow-2xl transition-all hover:scale-105 whitespace-nowrap cursor-pointer">
            ロッキングを開始
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}